import { Button } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AccountMovement } from '@/api';
import { formatAccountMovementFeatures } from '@/api/utils/account-movements';
import { DataTable } from '@/components/Ui/DataTable';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Section, SectionBody } from '@/components/Ui/Section';
import { downloadText } from '@/lib/messages';
import { formatEur, formatNumber, vfiatToEur } from '@/lib/money';
import { parseTimestamp, shortDate } from '@/lib/relative-time';
import { useRustyState } from '@/state';
import shared from '../AccountScreens.module.css';
import {
  isUsageKind,
  type MovementKind,
  movementDetail,
  movementKind,
  movementLabel,
} from './billing-labels';

const PAGE = 200;

interface HistoryRow {
  key: string;
  date: string;
  kind: MovementKind;
  raw: string;
  amount: number;
  /** Charges folded into a usage row. */
  count: number;
}

function dayKey(ts: string) {
  const d = parseTimestamp(ts);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** One row per day for usage charges (within or beyond the allowance), one row per movement otherwise; input is newest first. */
function groupRows(movements: AccountMovement[]): HistoryRow[] {
  const out: HistoryRow[] = [];
  const usage = new Map<string, HistoryRow>();
  for (const m of movements) {
    const kind = movementKind(m.features);
    if (isUsageKind(kind)) {
      const key = `usage:${dayKey(m.created_at)}`;
      const row = usage.get(key);
      if (row) {
        row.amount += m.amount;
        row.count += 1;
        continue;
      }
      const created: HistoryRow = {
        key,
        date: m.created_at,
        kind: 'usage',
        raw: m.features,
        amount: m.amount,
        count: 1,
      };
      usage.set(key, created);
      out.push(created);
      continue;
    }
    out.push({ key: m.id, date: m.created_at, kind, raw: m.features, amount: m.amount, count: 1 });
  }
  return out;
}

function signedEur(vfiat: number, lang: string) {
  if (vfiat === 0) return formatEur(0, lang, 2, 2);
  const abs = formatEur(vfiatToEur(Math.abs(vfiat)), lang, 2, 5);
  return vfiat > 0 ? `+${abs}` : `−${abs}`;
}

const csvCell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

export function HistorySection() {
  const { t, i18n } = useTranslation();
  const [movements, setMovements] = useState<AccountMovement[] | null>(null);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (offset: number) => {
    setBusy(true);
    try {
      const res = await useRustyState.getState().api.getAccountMovements({ limit: PAGE, offset });
      setMovements((prev) => (offset === 0 || !prev ? res.data : [...prev, ...res.data]));
      setTotal(res.total);
      setFailed(false);
    } catch {
      setFailed(true);
      setMovements((prev) => prev ?? []);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load(0);
  }, [load]);

  const rows = useMemo(() => groupRows(movements ?? []), [movements]);

  const details = (r: HistoryRow) => {
    if (r.kind === 'usage')
      return t('billing.history.usageDetail', {
        count: r.count,
        n: formatNumber(r.count, i18n.language),
      });
    if (r.kind === 'assistant') return formatAccountMovementFeatures(r.raw);
    return movementDetail(r.kind, t);
  };

  const what = (r: HistoryRow) =>
    r.kind === 'usage'
      ? t('billing.history.usageOn', { day: shortDate(r.date, i18n.language) })
      : movementLabel(r.kind, r.raw, t);

  const downloadCsv = () => {
    const header = ['date', 'what', 'identifier', 'amount_eur'];
    const lines = (movements ?? []).map((m) =>
      [
        parseTimestamp(m.created_at).toISOString(),
        movementLabel(movementKind(m.features), m.features, t),
        m.features,
        vfiatToEur(m.amount).toFixed(5),
      ]
        .map(csvCell)
        .join(',')
    );
    downloadText(
      [header.join(','), ...lines].join('\n'),
      `formshive-billing-history-${new Date().toISOString().slice(0, 10)}.csv`,
      'text/csv'
    );
  };

  return (
    <Section
      id="history"
      title={t('billing.history.title')}
      actions={
        <Button size="xs" variant="default" onClick={downloadCsv} disabled={!movements?.length}>
          {t('billing.history.csv')}
        </Button>
      }
    >
      {movements === null ? (
        <SectionBody>
          <EmptyText>{t('ui.loading')}</EmptyText>
        </SectionBody>
      ) : (
        <DataTable
          caption={t('billing.history.title')}
          rows={rows}
          rowKey={(r) => r.key}
          empty={failed ? t('billing.history.loadFailed') : t('billing.history.empty')}
          columns={[
            {
              key: 'date',
              header: t('billing.history.colDate'),
              render: (r) => (
                <span className={shared.nowrap}>{shortDate(r.date, i18n.language)}</span>
              ),
            },
            { key: 'what', header: t('billing.history.colWhat'), render: what },
            {
              key: 'details',
              header: t('billing.history.colDetails'),
              render: (r) => <span className={shared.sub}>{details(r)}</span>,
            },
            {
              key: 'credit',
              header: t('billing.history.colCredit'),
              align: 'num',
              render: (r) => signedEur(r.amount, i18n.language),
            },
          ]}
        />
      )}
      <div className={shared.footer}>
        <span className={shared.help}>
          {t('billing.history.footer')}
          {movements !== null && movements.length < total && (
            <> · {t('billing.history.footerLoaded', { loaded: movements.length, total })}</>
          )}
        </span>
        {movements !== null && movements.length < total && (
          <Button size="xs" variant="default" loading={busy} onClick={() => load(movements.length)}>
            {t('billing.history.older')}
          </Button>
        )}
      </div>
    </Section>
  );
}
